import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage, readBlobApiError } from '@digilize/shared/utils/helpers';
import { TranslocoService } from '@jsverse/transloco';

import { PaulyApiError, PaulyApiErrorInfo } from './error.interceptor';

/** The `{ error: { error: … } }` envelope every Pauly endpoint wraps a failure in. */
export function paulyApiError(err: unknown): PaulyApiError | null {
  return (err as HttpErrorResponse)?.error?.error ?? null;
}

/**
 * `info` is a single object on most failures, an array only on field validation ones, and a bare
 * kind string on a few. Reading it as whichever shape the caller happened to expect is what made the
 * payment step fail silently (PAUL-3197): `info[0].kind` threw on the object form, and the snackbar
 * below it never opened.
 */
export function apiErrorInfos(err: unknown): PaulyApiErrorInfo[] {
  const info = paulyApiError(err)?.info;
  if (!info) {
    return [];
  }
  return (Array.isArray(info) ? info : [info]).filter((entry) => entry && typeof entry === 'object');
}

/** Whether the backend named this rule among the reasons it refused — in either shape it names it. */
export function hasApiErrorKind(err: unknown, kind: string): boolean {
  const info: unknown = paulyApiError(err)?.info;
  if (typeof info === 'string') {
    return info === kind;
  }
  return apiErrorInfos(err).some((entry) => entry.kind === kind);
}

/** The id the backend logged this failure under — what makes a reported failure findable again. */
export function apiRequestId(err: unknown): string | null {
  return paulyApiError(err)?.request_id ?? null;
}

/**
 * Prefers whatever the backend said, from the most specific text to the least: `info.message`
 * describes what actually happened ("the requested integration was not found"), while `message` is
 * the generic envelope ("one or more fields contain an error"). `getErrorMessage` already picks
 * between those two, so only the shapes it cannot read are handled here — a plain `{ message }`
 * body, or a client-side/network failure with no body at all.
 */
export function apiErrorMessage(transloco: TranslocoService, err: unknown): string {
  const fromApi = getErrorMessage(paulyApiError(err), (key) => transloco.translate(key));
  if (fromApi) {
    return fromApi;
  }

  const body = (err as HttpErrorResponse)?.error;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message;
  }

  // Only a failure raised in the browser gets to speak for itself. An HttpErrorResponse whose body
  // said nothing readable has a technical `message` ("Http failure response for …: 404 Not Found")
  // that is no use to a buyer — the request id appended alongside is what makes it reportable.
  const clientMessage = err instanceof HttpErrorResponse ? null : (err as Error)?.message;
  if (clientMessage) {
    return clientMessage;
  }

  return transloco.translate('errors.something_went_wrong');
}

/**
 * A failed `responseType: 'blob'` request carries its JSON envelope as a `Blob`, which none of the
 * readers above can see into, so a refused download said nothing (PAUL-3904). Resolves to the same
 * error with the body decoded, or to the error untouched when there is nothing to decode.
 */
export async function withDecodedBlobBody(err: unknown): Promise<unknown> {
  if (!(err instanceof HttpErrorResponse) || !(err.error instanceof Blob)) {
    return err;
  }
  const decoded = await readBlobApiError(err);
  if (!decoded) {
    return err;
  }
  return new HttpErrorResponse({
    error: { error: decoded },
    headers: err.headers,
    status: err.status,
    statusText: err.statusText,
    url: err.url ?? undefined,
  });
}

/** What the API calls a cart whose hold has run out; the reservation it was for is gone with it. */
const CART_EXPIRED_ERROR_TYPES = ['cart_expired', 'cart_expired_error'];

/**
 * Whether the backend refused this request because the cart it was for no longer exists.
 *
 * Read at both levels the type has been seen at: `{ error: { error: { type } } }` is the envelope
 * every current endpoint answers with, and the bare `{ error: { type } }` is the older shape this was
 * originally written against. Reading only the bare one is what kept `CartExpiredInterceptor` silent
 * on every real refusal, so the expired dialog never opened and each step was left to notice the dead
 * cart for itself.
 */
export function isCartExpiredError(err: unknown): boolean {
  const type = paulyApiError(err)?.type ?? (err as HttpErrorResponse)?.error?.type;
  return typeof type === 'string' && CART_EXPIRED_ERROR_TYPES.includes(type);
}
